import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Eye, EyeOff, Mail, Lock, Loader2, ArrowRight, Shield, Check,
    LayoutDashboard, Building2, UsersRound, Boxes, Network, PanelsTopLeft, ListOrdered,
} from "lucide-react";
import { toast } from "sonner";
import { useDispatch } from "react-redux";

import { getLoginToken, getSuperAdminToken } from "@/redux/Auth/actions";
import { SET_USER_DETAIL } from "@/redux/Auth/constant";
import { sessionLogin } from "@/services/sessionClient";
import { setAuthToken } from "@/utility/CommonConstant";
import { useAuth } from "@/config/AuthContext";
import { getPostLoginPath } from "@/utils/agentUser";
import {
    TMS_BRAND,
    getBrandFromPath,
    applyTmsDocumentTitle,
    applyAuthenticatedTmsDocumentTitle,
} from "@/utils/tmsDocumentTitle";
import "./Login2.css";

const BG_VIDEO = `${process.env.PUBLIC_URL || ""}/hero-vodeo-2.mp4`;

const BG_FALLBACK_WEBP = `${process.env.PUBLIC_URL || ""}/loginpage-bg.webp`;
const BG_FALLBACK_PNG = `${process.env.PUBLIC_URL || ""}/loginpage-bg.png`;

const INFOX_LOGO_URL =
    "https://dm.infoxsystem.com/tms-app/logo-hd-infox.png";

const ACCENT = "AI Powered TMS";
const ACCENT_FONT = "'Inter', 'Roboto', system-ui, -apple-system, sans-serif";

const MODULES = [
    { label: "Platform Dashboard", detail: "System-wide overview", Icon: LayoutDashboard },
    { label: "Company Management", detail: "Organizations & profiles", Icon: Building2 },
    { label: "Users & Roles", detail: "Accounts & permissions", Icon: UsersRound },
    { label: "Module Catalog", detail: "Modules & submodules", Icon: Boxes },
    { label: "Navigation Builder", detail: "Menus & submenus", Icon: Network },
    // { label: "Company Navigation", detail: "Company-specific menus", Icon: PanelsTopLeft },
    { label: "Visibility & Ordering", detail: "Menu access & sequence", Icon: ListOrdered },
];

function getLogoForBrand(brand) {
    return INFOX_LOGO_URL;
}

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { setIsAuthenticated } = useAuth();

    // Determine if this is super admin login based on path
    const isSuperAdmin = location.pathname === "/signin";

    const [formData, setFormData] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(false);
    const [loading, setLoading] = useState(false);
    const [bgVideoReady, setBgVideoReady] = useState(false);
    const [bgVideoFailed, setBgVideoFailed] = useState(false);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const [showModules, setShowModules] = useState(false);
    const [bgImageSrc, setBgImageSrc] = useState(BG_FALLBACK_WEBP);

    // Session conflict state
    const [sessionConflict, setSessionConflict] = useState(null);
    const [conflictBusy, setConflictBusy] = useState(false);

    const loginBrand = getBrandFromPath(location.pathname || "");

    const [customLogo, setCustomLogo] = useState(() => getLogoForBrand(loginBrand));

    useEffect(() => {
        document.title = "Infox Super Admin";
    }, []);

    // Preload background image
    useEffect(() => {
        const bgPreload = document.createElement("link");
        bgPreload.rel = "preload";
        bgPreload.as = "image";
        bgPreload.href = BG_FALLBACK_WEBP;
        bgPreload.type = "image/webp";
        document.head.appendChild(bgPreload);

        return () => {
            if (bgPreload.parentNode) {
                bgPreload.parentNode.removeChild(bgPreload);
            }
        };
    }, []);

    // Update logo when brand changes
    useEffect(() => {
        setCustomLogo(getLogoForBrand(loginBrand));
    }, [loginBrand]);

    // Preload brand logo
    useEffect(() => {
        const logoHref = getLogoForBrand(loginBrand);
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = logoHref;
        document.head.appendChild(link);
        return () => {
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        };
    }, [loginBrand]);

    // Stagger the hero media and capability cards on entry.
    useEffect(() => {
        const videoTimer = window.setTimeout(() => setShouldLoadVideo(true), 1200);
        const modulesTimer = window.setTimeout(() => setShowModules(true), 300);
        return () => {
            window.clearTimeout(videoTimer);
            window.clearTimeout(modulesTimer);
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email || !formData.password) {
            toast.error("Please enter both email and password");
            return;
        }
        setLoading(true);

        const payload = JSON.stringify({
            username: formData.email,
            password: formData.password,
        });

        try {
            // Use appropriate auth action based on login type
            const authAction = getSuperAdminToken;
            const authToken = await dispatch(authAction(payload));

            if (authToken) {
                toast.success("Login successful!");
                applyAuthenticatedTmsDocumentTitle();
                setIsAuthenticated(true);
                navigate(isSuperAdmin ? "/admin" : "/signin");
            }
        } catch (error) {
            // console.log(error);

            // Handle session conflict
            if (error && error.sessionConflict) {
                setSessionConflict({
                    conflict: error.sessionConflict,
                    user: { email: formData.email, userID: null },
                });
                setLoading(false);
                return;
            }

            const msg =
                typeof error === "string"
                    ? error
                    : error?.message || error?.response?.data?.detail || "Login failed";
            toast.error(msg);
        }
        setLoading(false);
    };

    // Handle force continue (sign out other device)
    const forceContinueHere = async () => {
        if (!sessionConflict) return;
        setConflictBusy(true);
        try {
            // Retrieve pending user + token from localStorage
            let pendingUser = null,
                pendingToken = null,
                pendingResult = null;
            try {
                const u = localStorage.getItem("shipez.pendingUser");
                const t = localStorage.getItem("shipez.pendingToken");
                const r = localStorage.getItem("shipez.pendingResult");
                if (u) pendingUser = JSON.parse(u);
                if (t) pendingToken = t;
                if (r) pendingResult = JSON.parse(r);
            } catch (_) {
                /* ignore */
            }

            if (!pendingUser || !pendingToken) {
                toast.error("Session data expired — please sign in again.");
                setConflictBusy(false);
                setSessionConflict(null);
                return;
            }

            // Force sessionLogin — kicks the other device
            const res = await sessionLogin({ user: pendingUser, force: true });
            if (res && res.success) {
                const authUserPayload = pendingResult || pendingUser;
                const authUserDetail = Array.isArray(authUserPayload)
                    ? authUserPayload[0]
                    : authUserPayload;

                setAuthToken(pendingToken, "", "", authUserPayload);
                dispatch({ type: SET_USER_DETAIL, payload: authUserDetail });
                try {
                    localStorage.removeItem("shipez.pendingUser");
                    localStorage.removeItem("shipez.pendingToken");
                    localStorage.removeItem("shipez.pendingResult");
                } catch (_) {
                    /* ignore */
                }
                toast.success("Signed in — other device has been signed out.");
                setSessionConflict(null);
                applyAuthenticatedTmsDocumentTitle();
                setIsAuthenticated(true);
                navigate(isSuperAdmin ? "/admin" : getPostLoginPath());
            } else {
                toast.error("Could not take over the session. Please try again.");
            }
        } catch (err) {
            toast.error("Failed to sign in. " + (err?.message || ""));
        }
        setConflictBusy(false);
    };

    // Handle cancel conflict
    const cancelConflict = () => {
        setSessionConflict(null);
        try {
            localStorage.removeItem("authToken");
            localStorage.removeItem("authUser");
            localStorage.removeItem("shipez.session_id");
            localStorage.removeItem("shipez.pendingUser");
            localStorage.removeItem("shipez.pendingToken");
            localStorage.removeItem("shipez.pendingResult");
        } catch (_) {
            /* ignore */
        }
        toast.info("Sign-in cancelled. The other device is still signed in.");
    };

    const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    return (
        <div className="relative min-h-screen overflow-hidden bg-black text-slate-900" data-testid="login-page">
            {/* Background image — always visible; video layers on top when ready */}
            <div className="absolute inset-0 z-0" aria-hidden="true">
                <img
                    src={bgImageSrc}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                    decoding="sync"
                    loading="eager"
                    fetchPriority="high"
                    data-testid="login-bg-image"
                    onError={() => {
                        if (bgImageSrc !== BG_FALLBACK_PNG) {
                            setBgImageSrc(BG_FALLBACK_PNG);
                        }
                    }}
                />
            </div>

            {shouldLoadVideo && (
                <video
                    className={`absolute inset-0 z-[1] h-full w-full scale-105 object-cover transition-opacity duration-700 ${bgVideoReady && !bgVideoFailed ? "opacity-100" : "opacity-0"
                        }`}
                    src={BG_VIDEO}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                    data-testid="login-bg-video"
                    onCanPlay={() => setBgVideoReady(true)}
                    onError={() => {
                        setBgVideoFailed(true);
                        setBgVideoReady(false);
                    }}
                />
            )}

            <div className="pointer-events-none absolute inset-0 z-[2] bg-black/25" aria-hidden="true" />

            <div className="relative z-10 flex min-h-screen flex-col lg:flex-row">
                {/* Left Section - Hero Content */}
                <section className="flex flex-1 flex-col justify-start px-6 pb-8 pt-20 sm:px-10 sm:pb-12 lg:w-[56%] lg:justify-center lg:px-10 lg:py-12">
                    <div className="login2-hero-enter max-w-xl [text-shadow:0_2px_32px_rgba(0,0,0,0.45)]">
                        <p
                            className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/90 backdrop-blur-sm"
                            data-testid="login-brand-pill"
                        >
                            <Shield className="h-3.5 w-3.5" style={{ color: "#DC1414" }} />
                            Super Admin
                        </p>

                        <h1
                            className="whitespace-nowrap text-left font-semibold leading-[1.14] tracking-tight text-white"
                            style={{ fontSize: "clamp(1.15rem, calc(2.8vw - 5px), 2.9rem)" }}
                            data-testid="login-headline"
                        >
                            Admin Control Panel
                            {!isSuperAdmin && (
                                <span
                                    className="login2-accent whitespace-nowrap"
                                    style={{ color: "#DC1414", fontWeight: 200, fontFamily: ACCENT_FONT }}
                                >
                                    {ACCENT}
                                </span>
                            )}
                        </h1>

                        <p
                            className="mt-4 max-w-2xl text-left font-light leading-relaxed text-white/85"
                            style={{ fontSize: "clamp(0.8rem, calc(0.78vw + 0.36rem), 1.05rem)" }}
                        >
                            Manage your entire TMS platform from a single, secure dashboard.
                        </p>
                    </div>

                    {showModules && (
                        <div className="login2-modules-enter mt-8 w-full max-w-3xl">
                            <p className="mb-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/65 [text-shadow:0_2px_18px_rgba(0,0,0,0.5)]">
                                <span className="h-px w-7 bg-gradient-to-r from-transparent via-white/50 to-white/50" />
                                Super Admin Capabilities
                            </p>
                            <div
                                className="grid grid-cols-2 gap-3 sm:grid-cols-3"
                                data-testid="super-admin-capabilities"
                            >
                                {/* {MODULES.map(({ label, detail, Icon }) => (
                                    <div
                                        key={label}
                                        className="group flex min-w-0 items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.06] p-3 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/[0.1]"
                                    >
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-white/90 transition-colors group-hover:border-white/30">
                                            <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
                                        </span>
                                        <span className="min-w-0 text-left">
                                            <span className="block truncate text-[11px] font-semibold leading-snug text-white/90">
                                                {label}
                                            </span>
                                            <span className="mt-0.5 block truncate text-[10px] leading-snug text-white/55">
                                                {detail}
                                            </span>
                                        </span>
                                    </div>
                                ))} */}
                                {MODULES.map(({ label,detail, Icon }) => (
                                    <div
                                        key={label}
                                        className="login2-module login5-chip login5-card-beam group relative flex min-w-0 flex-col items-center gap-2 rounded-2xl border border-white/15 px-2.5 py-3.5 text-center transition-colors duration-300 hover:-translate-y-1"
                                    >
                                        <span className="relative z-[1] flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-white transition-all duration-300 group-hover:scale-110 group-hover:border-white/30">
                                            <Icon className="h-[21px] w-[21px]" strokeWidth={1.5} />
                                        </span>
                                        <span className="relative z-[1] text-[11.5px] font-medium leading-snug text-white/85 transition-colors duration-300 group-hover:text-white">
                                            {label}
                                        </span>
                                           <span className="mt-0.5 block truncate text-[10px] leading-snug text-white/55">
                                                {detail}
                                            </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </section>

                {/* Right Section - Login Form */}
                <section className="flex flex-1 items-stretch justify-center px-4 pb-10 pt-4 sm:px-6 lg:w-[44%] lg:items-center lg:py-12 lg:pr-10 xl:pr-14">
                    <div className="login5-beam-wrap relative w-full max-w-[420px]">
                        <div
                            className="login2-card-enter login5-glass relative z-[1] flex w-full flex-col rounded-[1.75rem] border border-white/[0.08] p-8 sm:p-9"
                            data-testid="login-card"
                        >
                            <div className="text-center">
                                <div className="inline-flex items-center justify-center px-3 py-2">
                                    <img
                                        src={customLogo}
                                        alt={"Info-X Logo"}
                                        className="h-11 w-auto max-w-[240px] object-contain sm:h-12 xl:h-[52px] xl:max-w-[260px]"
                                        draggable="false"
                                        decoding="async"
                                        fetchPriority="high"
                                        onError={() => {
                                            if (customLogo !== INFOX_LOGO_URL) {
                                                setCustomLogo(INFOX_LOGO_URL);
                                            }
                                        }}
                                    />
                                </div>
                                <h2 className="mt-5 text-lg font-semibold tracking-tight text-white">
                                    Super Admin Login
                                </h2>
                            </div>

                            <form onSubmit={handleSubmit} className="mt-8 flex flex-1 flex-col space-y-4">
                                <div>
                                    <label className="mb-1.5 block text-[13px] font-medium text-white/75">
                                        Email Address
                                    </label>
                                    <div className="group relative">
                                        <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35 transition-colors group-focus-within:text-white/80" />
                                        <input
                                            type="text"
                                            name="email"
                                            autoComplete="username"
                                            value={formData.email}
                                            onChange={onChange}
                                            placeholder="Enter your username"
                                            className="h-[52px] w-full rounded-2xl border border-white/25 bg-white/[0.04] pl-11 pr-3.5 text-sm text-white placeholder:text-white/30 shadow-[0_0_0_4px_rgba(255,255,255,0)] outline-none transition-[border-color,background-color,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] focus:border-white/30 focus:bg-white/[0.06] focus:shadow-[0_0_0_4px_rgba(255,255,255,0.06)]"
                                            data-testid="login-email"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="mb-1.5 block text-[13px] font-medium text-white/75">
                                        Password
                                    </label>
                                    <div className="group relative">
                                        <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35 transition-colors group-focus-within:text-white/80" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            autoComplete="current-password"
                                            value={formData.password}
                                            onChange={onChange}
                                            placeholder="Enter your password"
                                            className="h-[52px] w-full rounded-2xl border border-white/25 bg-white/[0.04] pl-11 pr-12 text-sm text-white placeholder:text-white/30 shadow-[0_0_0_4px_rgba(255,255,255,0)] outline-none transition-[border-color,background-color,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] focus:border-white/30 focus:bg-white/[0.06] focus:shadow-[0_0_0_4px_rgba(255,255,255,0.06)]"
                                            data-testid="login-password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((v) => !v)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-white/40 transition-colors hover:bg-white/10 hover:text-white"
                                            tabIndex={-1}
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            data-testid="login-toggle-password"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center pt-0.5">
                                    <label className="flex cursor-pointer select-none items-center gap-2.5">
                                        <button
                                            type="button"
                                            aria-pressed={remember}
                                            onClick={() => setRemember((v) => !v)}
                                            data-testid="login-remember"
                                            className={`flex h-[18px] w-[18px] items-center justify-center rounded-md border transition-all ${remember
                                                ? "border-white bg-white shadow-[0_0_0_3px_rgba(255,255,255,0.12)]"
                                                : "border-white/20 bg-white/[0.04] hover:border-white/40"
                                                }`}
                                        >
                                            {remember && <Check className="h-3 w-3 text-black" strokeWidth={3} />}
                                        </button>
                                        <span className="text-[13px] text-white/65">Remember me</span>
                                    </label>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="group relative mt-1 h-[52px] w-full overflow-hidden rounded-2xl border border-white/10 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:hover:translate-y-0"
                                    style={{
                                        background: isSuperAdmin
                                            ? "linear-gradient(135deg, #1e293b 0%, #334155 100%)"
                                            : "#000000",
                                        boxShadow: "0 14px 36px -12px rgba(0,0,0,0.55), 0 8px 20px -8px rgba(0,0,0,0.35)",
                                    }}
                                    data-testid="login-submit"
                                >
                                    <span
                                        className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                                        aria-hidden="true"
                                        style={{ background: "#111111" }}
                                    />
                                    <span className="relative inline-flex items-center justify-center gap-2">
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Signing in…
                                            </>
                                        ) : (
                                            <>
                                                Sign In
                                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                                            </>
                                        )}
                                    </span>
                                </button>
                            </form>
                        </div>
                    </div>
                </section>
            </div>

            {/* Session Conflict Modal */}
            {sessionConflict && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                    style={{ background: "rgba(0,4,20,0.86)", backdropFilter: "blur(8px)" }}
                >
                    <div
                        className="w-full max-w-lg rounded-2xl overflow-hidden"
                        style={{ background: "#0b1830", border: "1px solid #1e3a8a" }}
                        data-testid="session-conflict-modal"
                    >
                        <div
                            className="px-6 py-4 flex items-center gap-3"
                            style={{ borderBottom: "1px solid #1e293b" }}
                        >
                            <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center"
                                style={{ background: "rgba(244,63,94,0.15)", color: "#fda4af", border: "1px solid rgba(244,63,94,0.3)" }}
                            >
                                <Lock className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold" style={{ color: "#f8fafc" }}>
                                    Same user is signed in on another device
                                </h3>
                                <p className="text-xs mt-0.5" style={{ color: "#94a3b8" }}>
                                    Only one active session is allowed per user.
                                </p>
                            </div>
                        </div>
                        <div className="px-6 py-4 space-y-3 text-sm" style={{ color: "#cbd5e1" }}>
                            <p>
                                <strong style={{ color: "#f8fafc" }}>{formData.email}</strong> is currently
                                signed in on another computer. If you continue here, that device will be
                                signed out automatically.
                            </p>
                            {sessionConflict.conflict.active_session && (
                                <div
                                    className="rounded-lg p-3 space-y-1.5"
                                    style={{ background: "rgba(15,23,42,0.6)", border: "1px solid #334155" }}
                                >
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                            <div style={{ color: "#64748b" }}>Device</div>
                                            <div style={{ color: "#e2e8f0" }}>
                                                {sessionConflict.conflict.active_session.device_name || "—"}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ color: "#64748b" }}>Browser / OS</div>
                                            <div style={{ color: "#e2e8f0" }}>
                                                {sessionConflict.conflict.active_session.browser}{" "}
                                                {sessionConflict.conflict.active_session.os && (
                                                    <span style={{ color: "#94a3b8" }}>
                                                        · {sessionConflict.conflict.active_session.os}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ color: "#64748b" }}>IP</div>
                                            <div style={{ color: "#e2e8f0" }}>
                                                {sessionConflict.conflict.active_session.ip_address || "—"}
                                                {sessionConflict.conflict.active_session.local_ip && (
                                                    <span style={{ color: "#94a3b8" }}>
                                                        {" "}(LAN {sessionConflict.conflict.active_session.local_ip})
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ color: "#64748b" }}>Location</div>
                                            <div style={{ color: "#e2e8f0" }}>
                                                {sessionConflict.conflict.active_session.city || "—"}
                                                {sessionConflict.conflict.active_session.country && (
                                                    <span style={{ color: "#94a3b8" }}>
                                                        , {sessionConflict.conflict.active_session.country}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ color: "#64748b" }}>Signed in</div>
                                            <div style={{ color: "#e2e8f0" }}>
                                                {sessionConflict.conflict.active_session.created_at
                                                    ? new Date(
                                                        sessionConflict.conflict.active_session.created_at
                                                    ).toLocaleString()
                                                    : "—"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <p style={{ color: "#94a3b8", fontSize: "12px" }}>
                                Tip: if this wasn't you, please review the login history under System
                                Maintenance and change your password.
                            </p>
                        </div>
                        <div
                            className="px-6 py-4 flex items-center justify-end gap-2"
                            style={{ borderTop: "1px solid #1e293b", background: "rgba(15,23,42,0.4)" }}
                        >
                            <button
                                onClick={cancelConflict}
                                disabled={conflictBusy}
                                data-testid="session-cancel-btn"
                                className="px-4 py-2 rounded-lg text-sm"
                                style={{ background: "#1e293b", color: "#cbd5e1" }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={forceContinueHere}
                                disabled={conflictBusy}
                                data-testid="session-force-btn"
                                className="px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2"
                                style={{
                                    background: "linear-gradient(90deg, #84cc16, #22d3ee)",
                                    color: "#0f172a",
                                }}
                            >
                                {conflictBusy ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
                                ) : (
                                    <>Sign out other device & continue</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;